import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Coins,
  HeartHandshake,
  MessageCircle,
  Gamepad2,
  Server,
  Sword,
  Flag,
  ArrowRight,
} from "lucide-react";
import { CopyIpButton } from "@/components/CopyIpButton";
import { IMAGES, DISCORD_URL, SUPPORTED_VERSIONS } from "@/data/site";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0 },
};

const features = [
  {
    icon: Coins,
    title: "Economia Dinamica e Bilanciata",
    text: "Un'economia reale che risponde alle tue scelte. Compra, vendi e specula sul mercato interno tramite l'Auction House (/ah) e gli scambi sicuri (/trade). I prezzi fluttuano in base alla domanda: niente inflazione incontrollata, niente item \"stampati\" dal nulla. Qui la ricchezza si costruisce con la strategia, non con i bug.",
  },
  {
    icon: HeartHandshake,
    title: "Community Attiva e Staff Maturo",
    text: "Migliaia di ore di gioco condivise, eventi periodici e uno staff adulto, preparato e imparziale. Qui trovi persone, non bot: alleanze, mercati, guerre commerciali e amicizie reali. Il nostro team interviene con rapidità e trasparenza, mantenendo la Giungla un posto sano per tutti.",
  },
];

const steps = [
  {
    n: "1",
    icon: Gamepad2,
    title: "Avvia Minecraft",
    text: "Apri Minecraft Java Edition in versione 26.2 e vai su \"Multiplayer\" → \"Aggiungi Server\".",
  },
  {
    n: "2",
    icon: Server,
    title: "Inserisci l'IP",
    text: "Incolla l'indirizzo lagiungla.it nel campo \"Indirizzo Server\". Salva e connettiti al mondo.",
  },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.hero})` }}
        />
        <div className="absolute inset-0 bg-[#0f1410]/75" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f1410] via-[#0f1410]/30 to-[#0f1410]/60" />
        <div className="grain" />

        <motion.div
          initial="hidden"
          animate="show"
          transition={{ staggerChildren: 0.15, delayChildren: 0.1 }}
          className="relative z-10 max-w-4xl mx-auto px-5 text-center pt-24 pb-16"
        >
          <motion.h1
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="font-heading font-bold uppercase text-5xl md:text-7xl lg:text-8xl tracking-tight text-white leading-none"
          >
            La Giungla <span className="text-gradient-gold">SMP</span>
          </motion.h1>
          <motion.h2
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="font-heading uppercase mt-4 text-lg md:text-2xl tracking-[0.2em] text-[#d4af37]"
          >
            Sopravvivi. Costruisci. Domina la Giungla.
          </motion.h2>
          <motion.p
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="mt-6 text-base md:text-lg text-[#e0e0e0] max-w-2xl mx-auto leading-relaxed"
          >
            Benvenuto nel survival italiano definitivo. Un mondo persistente dove
            ogni blocco che piazzi conta, ogni alleanza pesa e ogni impero nasce
            dal nulla. Niente pay-to-win distruttivo, niente caos: solo survival
            puro, un'economia viva e una community che ti aspetta.
          </motion.p>
          <motion.p
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="mt-5 text-sm text-[#9fb0a3]"
          >
            🌿 Server ottimizzato • Anti-cheat attivo • Zero lag • Staff presente
            24/7
          </motion.p>

          <motion.div
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <CopyIpButton
              testId="hero-copy-ip"
              subtitle={`Versioni supportate: ${SUPPORTED_VERSIONS}`}
            />
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-discord-btn"
              className="w-full sm:w-auto border-2 border-[#d4af37] text-white hover:bg-[#d4af37] hover:text-[#0f1410] transition-all duration-300 px-8 py-4 rounded-none uppercase font-heading font-bold tracking-widest flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              Entra nel Discord
            </a>
          </motion.div>
        </motion.div>
      </section>

      {/* PERCHÉ SCEGLIERCI */}
      <section className="relative max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-32">
        <div className="w-24 h-1 bg-[#1b4d22] mx-auto mb-8" />
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={fadeUp}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto"
        >
          <h2 className="font-heading font-bold uppercase text-3xl md:text-5xl tracking-tight text-white">
            Perché giocare su La Giungla SMP
          </h2>
          <p className="mt-5 text-base md:text-lg text-[#e0e0e0]">
            Non siamo l'ennesimo survival abbandonato. Siamo un ecosistema curato
            nei minimi dettagli, dove la longevità del tuo lavoro è garantita.
          </p>
        </motion.div>

        <div className="mt-16 grid gap-8 md:grid-cols-2 max-w-4xl mx-auto">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              data-testid={`feature-card-${i}`}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ duration: 0.5, delay: i * 0.12 }}
              className="bg-[#141b15] border border-[#1b4d22]/50 p-8 hover:-translate-y-2 hover:border-[#d4af37]/60 transition-all duration-300 group"
            >
              <div className="w-16 h-16 rounded-full bg-[#0f1410] border border-[#1b4d22] flex items-center justify-center mb-6 group-hover:gold-glow transition-all">
                <f.icon className="w-8 h-8 text-[#d4af37]" />
              </div>
              <h3 className="font-heading font-bold uppercase text-xl tracking-wide text-[#d4af37] mb-3">
                {f.title}
              </h3>
              <p className="text-sm md:text-base text-[#c8d3cb] leading-relaxed">
                {f.text}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* GUIDA RAPIDA */}
      <section className="relative bg-[#0c110d] border-y border-[#1b4d22]/40 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="font-heading font-bold uppercase text-3xl md:text-5xl tracking-tight text-white">
              Inizia a giocare in <span className="text-[#d4af37]">60 secondi</span>
            </h2>
            <p className="mt-5 text-base md:text-lg text-[#e0e0e0]">
              Due passi ti separano dalla tua avventura nella Giungla.
            </p>
          </motion.div>

          <div className="mt-16 grid gap-8 md:grid-cols-2 max-w-3xl mx-auto relative">
            {steps.map((s, i) => (
              <motion.div
                key={s.n}
                data-testid={`step-card-${s.n}`}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: i * 0.15 }}
                className="relative text-center px-4"
              >
                <div className="mx-auto w-20 h-20 rounded-full border-2 border-[#d4af37] flex items-center justify-center mb-6 bg-[#0f1410] gold-glow">
                  <span className="font-heading font-bold text-4xl text-[#d4af37]">
                    {s.n}
                  </span>
                </div>
                <s.icon className="w-8 h-8 text-[#1b8a2f] mx-auto mb-4" />
                <h3 className="font-heading font-bold uppercase text-xl tracking-wide text-white mb-3">
                  {s.title}
                </h3>
                <p className="text-sm md:text-base text-[#c8d3cb] leading-relaxed">
                  {s.text}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 flex flex-col items-center gap-6">
            <CopyIpButton testId="quickstart-copy-ip" />
          </div>
        </div>
      </section>
    </div>
  );
}
